import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Zap, Sparkles, Image as ImageIcon, Info, CheckCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';

const PUBLISH_INTAKE_KEY = 'consistency_publish_intake';
const VARIANTS_KEY = 'consistency_optimized_variants';
const API_URL = window.__BACKEND_URL;

const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', aspect: 'LF 16:9' },
  { id: 'tiktok', name: 'TikTok', aspect: 'SF 9:16' },
  { id: 'instagram', name: 'Instagram', aspect: 'SF 9:16' },
  { id: 'facebook', name: 'Facebook', aspect: 'LF 16:9' }
];

export default function OptimizeContent({ prefilledAssets = [] } = {}) {
  const [assets, setAssets] = useState([]);
  const [variants, setVariants] = useState([]);  

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hashtags, setHashtags] = useState(''); 
  const [thumbnailLF, setThumbnailLF] = useState('');
  const [thumbnailSF, setThumbnailSF] = useState('');

  useEffect(() => {
    loadAssets();
    loadVariants();
  }, []);

  // When the selected asset is a real ContentSource (came from Project
  // Overview / Project Upload, not the mocked intake list), pull its
  // persisted default thumbnail so the Scheduler starts from the correct
  // thumbnail for that video instead of an empty upload prompt. The
  // existing manual LF/SF upload controls still override this.
  useEffect(() => {
    if (!selectedAsset?.id || !selectedAsset?.creatorId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/content-sources/${selectedAsset.id}/thumbnail`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!data.thumbnail_url || cancelled) return;
        setThumbnailLF((prev) => prev || data.thumbnail_url);
        setThumbnailSF((prev) => prev || data.thumbnail_url);
      } catch {
        // No persisted thumbnail yet — leave the manual upload controls as the only option.
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAsset?.id, selectedAsset?.creatorId]);
  
  const loadAssets = () => {
    const intakeRaw = localStorage.getItem(PUBLISH_INTAKE_KEY);
    const intake = intakeRaw ? JSON.parse(intakeRaw) : [];
    const approved = intake.filter((item) => item.status === 'approved');

    // Real asset(s) handed off from Project Overview / Project Upload's
    // "Schedule" action — stable ContentSource ids (`asset.id`), not
    // filenames, so they carry through into any variant/post created from
    // them below. Seeded in addition to the existing (mocked) intake list,
    // never replacing it.
    const realAssets = (prefilledAssets || []).map((a) => ({
      id: a.id,
      filename: a.title || 'Uploaded asset',
      source: 'Your project',
      creatorId: a.creatorId,
      status: 'approved',
    }));
    const merged = [...realAssets, ...approved.filter((a) => !realAssets.some((r) => r.id === a.id))];
    setAssets(merged);
    if (realAssets.length > 0 && !selectedAsset) {
      setSelectedAsset(realAssets[0]);
    }
  };
  
  const loadVariants = () => {
    const variantsRaw = localStorage.getItem(VARIANTS_KEY);
    const stored = variantsRaw ? JSON.parse(variantsRaw) : [];
    setVariants(stored);
  };

  const handlePlatformToggle = (platformId) => {
    if (selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(selectedPlatforms.filter((p) => p !== platformId));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platformId]);
    }
  };

  const handleGenerateAISuggestions = () => {
    if (!selectedAsset) {
      toast.error('Please select an asset first');
      return;
    }
    
    const assetTitle = selectedAsset.filename || selectedAsset.title || 'Untitled';
    const suggestions = {
      youtube: {
        title: `${assetTitle} | Full Guide`,
        description: `In this video, we cover everything about ${assetTitle}. Don't forget to like and subscribe!`,
        hashtags: '#YouTube #Content #Tutorial'
      },
      tiktok: {
        title: `${assetTitle} 🔥`,
        description: `Quick tip: ${assetTitle}`,
        hashtags: '#TikTok #Viral #ForYou #Tips'
      },
      instagram: {
        title: `${assetTitle} ✨`,
        description: `Check out this amazing ${assetTitle}!`,
        hashtags: '#Instagram #Reels #Trending'
      },
      facebook: {
        title: assetTitle,
        description: `Watch our latest content: ${assetTitle}`,
        hashtags: '#Facebook #Video #Content'
      }
    };
    
    const platform = selectedPlatforms[0] || 'youtube';
    const suggestion = suggestions[platform]; 

    setTitle(suggestion.title);
    setDescription(suggestion.description);
    setHashtags(suggestion.hashtags); 

    toast.success('AI suggestions generated!');
  };
  
  const readFileAsDataUrl = (file, setter) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setter(reader.result.toString());
    };
    reader.readAsDataURL(file);
  };

  const handleCreateVariant = () => {
    if (!selectedAsset) {
      toast.error('Please select an asset');
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!thumbnailLF || !thumbnailSF) {
      toast.error('Please upload both thumbnails');
      return;
    }

    const newVariant = {
      id: `variant_${Date.now()}`,
      creatorId: selectedAsset.creatorId,
      assetId: selectedAsset.id,
      platforms: selectedPlatforms,
      title: title.trim(),
      description: description.trim(),
      hashtags: hashtags.trim(),
      brandingApplied: true,
      thumbnailLF,
      thumbnailSF,
      status: 'ready',
      createdAt: new Date().toISOString()
    };

    const updatedVariants = [...variants, newVariant];
    setVariants(updatedVariants);
    localStorage.setItem(VARIANTS_KEY, JSON.stringify(updatedVariants));
    toast.success('Optimized variant created!');

    setTitle('');
    setDescription('');
    setHashtags('');
    setSelectedPlatforms([]);
    setThumbnailLF('');
    setThumbnailSF('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>Optimization Stage:</strong> AI-first suggestions for title, description, hashtags. Branding applied here. Create optimized variants to feed the Scheduler.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Asset Selection & Optimization */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-pink-500" />
              Create Optimized Variant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Asset Selection */}
            <div>
              <Label>Select Asset</Label>
              <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                {assets.length === 0 ? (   
                  <p className="text-sm text-[#8A93A6] text-center py-4">No approved assets available. Approve clips in Content Studio first.</p>
                ) : (      
                  assets.map((asset) => (
                    <div
                      key={asset.id}
                      onClick={() => setSelectedAsset(asset)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedAsset?.id === asset.id
                          ? 'border-2 border-pink-500 bg-pink-500/10'
                          : 'border border-white/10 hover:border-white/20'
                      }`}
                    >
                      <p className="text-sm text-white font-medium">{asset.filename || asset.title || 'Untitled'}</p>
                      <p className="text-xs text-[#8A93A6]">{asset.source}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Platform Selection */}
            <div>
              <Label>Target Platforms *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {PLATFORMS.map((platform) => (
                  <div
                    key={platform.id}
                    onClick={() => handlePlatformToggle(platform.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedPlatforms.includes(platform.id)
                        ? 'border-2 border-pink-500 bg-pink-500/10'
                        : 'border border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox checked={selectedPlatforms.includes(platform.id)} />
                      <div>
                        <p className="text-sm text-white">{platform.name}</p>
                        <p className="text-xs text-[#8A93A6]">{platform.aspect}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Suggestions */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-pink-500" />
                <span className="text-sm text-white">AI Generate Suggestions</span>
              </div>
              <Button size="sm" variant="outline" className="border-pink-500/30 text-pink-500">
                Generate
              </Button>
            </div>

            {/* Title */}
            <div>
              <Label>Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                className="glass mt-1"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                className="glass mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label>Hashtags</Label>
              <Input
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="#example #tags"
                className="glass mt-1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Thumbnail (LF 16:9)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      readFileAsDataUrl(file, setThumbnailLF);
                    }
                  }}
                  className="glass mt-1"
                />
                {thumbnailLF && <p className="text-xs text-[#8A93A6] mt-1">Thumbnail ready</p>}
              </div>
              <div>
                <Label>Thumbnail (SF 9:16)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      readFileAsDataUrl(file, setThumbnailSF);
                    }
                  }}
                  className="glass mt-1"
                />
                {thumbnailSF && <p className="text-xs text-[#8A93A6] mt-1">Thumbnail ready</p>}
              </div>
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-green-400">
                <CheckCircle className="h-4 w-4" />
                <span>Branding applied: Symbol, captions, progress bar</span>
              </div>
            </div>

            <Button
              onClick={handleCreateVariant}
              disabled={!selectedAsset || selectedPlatforms.length === 0 || !title.trim()}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Optimized Variant
            </Button>
          </CardContent>
        </Card>

        {/* Right: Created Variants */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Optimized Variants</CardTitle>
          </CardHeader>
          <CardContent>
            {variants.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="h-12 w-12 text-[#8A93A6] mx-auto mb-4" />
                <p className="text-[#8A93A6]">No optimized variants yet</p>
                <p className="text-sm text-[#8A93A6] mt-2">Create your first variant to feed the Scheduler</p>
              </div>
            ) : (
              <div className="space-y-3">
                {variants.map((variant) => (
                  <div key={variant.id} className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium text-white">{variant.title}</h3>
                        <p className="text-xs text-[#8A93A6]">Linked asset: {variant.assetId}</p>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">
                        {variant.platforms?.join(', ')}
                      </Badge>
                    </div>
                    {variant.description && (
                      <p className="text-xs text-[#8A93A6] mb-2 line-clamp-2">{variant.description}</p>
                    )}
                    {variant.hashtags && (
                      <p className="text-xs text-blue-400">{variant.hashtags}</p>
                    )}
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {variant.thumbnailLF && (
                        <img src={variant.thumbnailLF} alt={`${variant.title} LF thumbnail`} className="rounded border border-white/10" />
                      )}
                      {variant.thumbnailSF && (
                        <img src={variant.thumbnailSF} alt={`${variant.title} SF thumbnail`} className="rounded border border-white/10" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-[#8A93A6]">
                      <CheckCircle className="h-3 w-3 text-green-500" /> 
                      <span>Status: {variant.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}                                                                                     